import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from '../../lib/cx';
import { IconButton } from './Button';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

// Nested dialogs: only the top-most one handles Escape and Tab trapping.
const openStack = [];

const PANEL_VARIANTS = {
  center: 'relative m-auto w-full max-h-[min(90dvh,48rem)] rounded-2xl border border-line animate-pop-in',
  right: 'ml-auto h-full w-full border-l border-line animate-slide-in-right',
  left: 'mr-auto h-full w-full border-r border-line animate-slide-in-left',
  bottom: 'mt-auto w-full max-h-[90dvh] rounded-t-2xl border-t border-line animate-slide-in-up',
  full: 'h-full w-full animate-fade-in',
};

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-3xl', full: 'max-w-none' };

/**
 * Accessible modal dialog / drawer / bottom sheet rendered in a portal.
 * Traps focus, closes on Escape or backdrop click, locks page scroll and
 * returns focus to the element that opened it.
 *
 * `variant`: center | right | left | bottom | full. `hideHeader` lets
 * children render their own header (they must then label the dialog via
 * `labelledBy`).
 */
export const Dialog = ({
  open,
  onClose,
  title,
  description,
  variant = 'center',
  size = 'md',
  children,
  footer,
  hideHeader = false,
  labelledBy,
  initialFocusRef,
  className,
  bodyClassName,
}) => {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openStack.push(token);
    const previouslyFocused = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const target = initialFocusRef?.current
        || panelRef.current?.querySelector('[data-autofocus]')
        || panelRef.current?.querySelector(FOCUSABLE)
        || panelRef.current;
      target?.focus({ preventScroll: true });
    }, 0);

    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      openStack.splice(openStack.indexOf(token), 1);
      if (!openStack.length) document.body.style.overflow = overflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  const isSide = variant === 'right' || variant === 'left';

  return createPortal(
    <div className={cx('fixed inset-0 z-50 flex', variant === 'center' && 'items-center justify-center p-4')}>
      <div
        className="absolute inset-0 animate-fade-in bg-black/55 backdrop-blur-[2px]"
        aria-hidden="true"
        onMouseDown={() => onCloseRef.current()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (hideHeader ? undefined : titleId)}
        aria-label={!labelledBy && hideHeader ? title : undefined}
        aria-describedby={description && !hideHeader ? descId : undefined}
        tabIndex={-1}
        className={cx(
          'relative z-10 flex flex-col bg-surface text-fg shadow-overlay focus:outline-none',
          PANEL_VARIANTS[variant],
          (variant === 'center' || isSide || variant === 'bottom') && SIZES[size],
          className,
        )}
      >
        {!hideHeader && (
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <h2 id={titleId} className="text-base font-semibold text-fg">{title}</h2>
              {description && <p id={descId} className="mt-0.5 text-sm text-muted">{description}</p>}
            </div>
            <IconButton icon={X} label="Close" onClick={() => onCloseRef.current()} className="-mr-2 -mt-1" />
          </div>
        )}
        <div className={cx('min-h-0 flex-1 overflow-y-auto scrollbar-thin', !hideHeader && 'px-5 py-4', bodyClassName)}>
          {children}
        </div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
